import { DialogComponent } from '@theme/dialog';
import { Wishlist } from '@theme/wishlist-storage';
import { CartAddEvent, ThemeEvents } from '@theme/events';

/**
 * Wishlist drawer.
 *
 * Opens as a right-hand drawer (same pattern as the cart drawer), reads items
 * from {@link Wishlist} and lazy-renders them by fetching product cards via
 * Shopify's Section Rendering API. Falls back to recently viewed + a
 * configurable bestsellers collection when the wishlist is empty.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} list - Container that holds the wishlist items.
 * @property {HTMLElement} emptyState - Empty-state container.
 * @property {HTMLElement} emptyStateList - Container for the recommended products in the empty state.
 * @property {HTMLElement} emptyStateTitle - Heading of the empty state recommendations.
 * @property {HTMLElement} [count] - Badge that mirrors the wishlist count.
 *
 * @extends {DialogComponent}
 */
export class WishlistDrawerComponent extends DialogComponent {
  /**
   * When `true`, calls to {@link closeDialog} are ignored. We flip this on
   * around any in-drawer interaction that synchronously rearranges the DOM
   * (the remove button being the obvious one): if DialogComponent's own
   * outside-click handler somehow still fires AND its target ends up as a
   * detached node, `isClickedOutside` returns true and the drawer would
   * collapse. Suppressing the close for one event-loop tick neutralises
   * every possible code path without us having to chase each one.
   * @type {boolean}
   */
  #suppressClose = false;

  /**
   * Captured reference to {@link DialogComponent.closeDialog} so we can
   * delegate to the original implementation once the suppression window has
   * elapsed.
   * @type {(() => Promise<void>) | undefined}
   */
  #originalCloseDialog;

  connectedCallback() {
    // Wrap the parent's `closeDialog` BEFORE any of our event wiring runs so
    // that every subsequent caller (including the parent class's own
    // outside-click handler) goes through our suppression check.
    if (!this.#originalCloseDialog) {
      this.#originalCloseDialog = this.closeDialog.bind(this);
      this.closeDialog = async () => {
        if (this.#suppressClose) {
          console.warn(
            '[wishlist] closeDialog suppressed during in-drawer interaction'
          );
          return;
        }
        return this.#originalCloseDialog();
      };
    }

    super.connectedCallback();
    window.addEventListener(Wishlist.CHANGE_EVENT, this.#handleWishlistChange);
    window.addEventListener('pageshow', this.#handlePageShow);
    window.addEventListener('pagereveal', this.#handlePageShow);
    window.addEventListener('storage', this.#handleStorage);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    // Single delegated click listener on the drawer itself — survives every
    // section-morph because the drawer element stays in the DOM. Replaces
    // the per-button addEventListener calls inside #renderItem.
    this.addEventListener('click', this.#handleDelegatedClick);
    // The theme's `morph` calls `onAfterUpdate(newNode)` with the freshly-
    // parsed source element (not our instance in the DOM), so
    // `updatedCallback` never actually fires for our component. Instead we
    // listen to the cart-update event directly — by the time the event has
    // bubbled, the morph has finished wiping our JS-rendered list, so we
    // schedule a re-render in the next microtask.
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.addEventListener('dialog:open', this.#handleOpen);
    this.#renderCountBadge();
    this.#render();
  }

  /**
   * Called by the Component base class after the section that contains this
   * drawer has been morphed (e.g. when the cart-items section refreshes after
   * an add-to-cart and our drawer happens to share the header section). The
   * morph replaces our JS-injected `<li>` items with the empty Liquid markup.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#renderCountBadge();
    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(Wishlist.CHANGE_EVENT, this.#handleWishlistChange);
    window.removeEventListener('pageshow', this.#handlePageShow);
    window.removeEventListener('pagereveal', this.#handlePageShow);
    window.removeEventListener('storage', this.#handleStorage);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    this.removeEventListener('dialog:open', this.#handleOpen);
    this.removeEventListener('click', this.#handleDelegatedClick);
  }

  /**
   * Single delegated click handler for the drawer. Matches against the live
   * DOM so it works for items rendered before AND after any section morph.
   * @param {MouseEvent} event
   */
  #handleDelegatedClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target || !target.closest) return;

    const addBtn = target.closest('.wishlist-drawer__add');
    if (addBtn) {
      const variantId = addBtn.dataset.variantId;
      console.log('[wishlist] add-to-cart click fired, variantId=', variantId);
      if (variantId) {
        event.preventDefault();
        event.stopPropagation();
        // Stop sibling listeners on the same element from running — keeps
        // the parent DialogComponent's outside-click handler quiet.
        event.stopImmediatePropagation();
        this.#handleAddToCart(addBtn, variantId);
      }
      return;
    }

    const removeBtn = target.closest('.wishlist-drawer__remove');
    if (removeBtn) {
      const productId = removeBtn.dataset.productId;
      if (productId) {
        // DialogComponent (our parent class) listens on the same element
        // and closes the dialog when `isClickedOutside()` returns true.
        // Since stopPropagation() doesn't stop sibling listeners on the
        // SAME element, we need stopImmediatePropagation() so the dialog's
        // outside-click check never runs for this event.
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        console.log('[wishlist] remove click for productId=', productId);

        // Move focus to a safe spot before deletion so the browser doesn't
        // see the focused element disappear and shift focus outside the
        // dialog.
        const closeBtn = this.querySelector('.wishlist-drawer__close-button');
        if (closeBtn) {
          try { closeBtn.focus({ preventScroll: true }); } catch (_e) {}
        }

        // Defer the actual removal until AFTER the click event fully
        // unwinds. Otherwise the DialogComponent's outside-click handler
        // (if it somehow still runs) sees a DOM-orphaned target and treats
        // it as a click outside the dialog → closeDialog().
        queueMicrotask(() => {
          Wishlist.remove(String(productId));
        });
      }
    }
  };

  /**
   * Re-sync when another tab modifies localStorage for our wishlist key.
   * Fires the wishlist:change event so wishlist buttons across the page
   * also update their heart state.
   * @param {StorageEvent} event
   */
  #handleStorage = (event) => {
    if (event.key !== Wishlist.STORAGE_KEY && event.key !== null) return;
    this.#renderCountBadge();
    // Bubble the change locally so wishlist-button instances re-sync too.
    window.dispatchEvent(
      new CustomEvent(Wishlist.CHANGE_EVENT, {
        detail: { source: 'storage' },
      })
    );
  };

  /**
   * Re-sync when the tab is foregrounded again — covers the case where the
   * customer added/removed wishlist items in another tab.
   */
  #handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.#renderCountBadge();
    }
  };

  /**
   * Re-render after a cart-update event. The cart-items section morphs the
   * header (which contains us) and wipes our JS-rendered list. Scheduling
   * in a microtask lets the morph complete before we repaint.
   * @param {CustomEvent} _event
   */
  #handleCartUpdate = (_event) => {
    queueMicrotask(() => {
      this.#renderCountBadge();
      this.#render();
    });
  };

  /**
   * Re-syncs the badge whenever the page is shown — covers reloads, SPA
   * navigation and the back/forward bfcache restore where connectedCallback
   * does not fire.
   * @param {PageTransitionEvent} _event
   */
  #handlePageShow = (_event) => {
    this.#renderCountBadge();
  };

  /** Public API used by the on:click bindings. */
  open() {
    this.showDialog();
  }

  /** Public API used by the on:click bindings. */
  close() {
    this.closeDialog();
  }

  /**
   * Public API: remove a single item. Buttons inside the drawer wire up their
   * own click listeners (more reliable for dynamically-rendered markup), but
   * this method stays available for programmatic use and the theme's on:click
   * delegation pattern.
   *
   * @param {Event | { target?: HTMLElement }} event
   */
  removeItem(event) {
    if (event && 'preventDefault' in event && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }
    const trigger = /** @type {HTMLElement | null} */ (event && (event.target || null));
    const button = trigger && trigger.closest ? trigger.closest('[data-product-id]') : null;
    const productId = button && button.dataset ? button.dataset.productId : null;
    if (!productId) return;
    Wishlist.remove(productId);
  }

  /** Clears the entire wishlist — bound via `on:click`. */
  clearAll(event) {
    event?.preventDefault?.();
    Wishlist.clear();
  }

  #handleOpen = () => {
    this.#render();
  };

  /**
   * @param {CustomEvent} event
   */
  #handleWishlistChange = (event) => {
    this.#renderCountBadge();

    const action = event && event.detail && event.detail.action;
    const changedId = event && event.detail && event.detail.id;

    // Targeted removal — surgically take the deleted item out of the DOM
    // instead of clearing the whole list and re-rendering with skeletons.
    // That kept the drawer visually collapsing / flashing for the user.
    if (action === 'remove' && changedId) {
      const list = this.#findRef('list');
      if (list) {
        const li = list.querySelector(`[data-product-id="${changedId}"]`);
        if (li) li.remove();
        if (list.children.length === 0) {
          this.#showEmptyState();
        }
        return;
      }
    }

    // Targeted clear — keep the empty-state visible without churn.
    if (action === 'clear') {
      const list = this.#findRef('list');
      if (list) list.innerHTML = '';
      this.#showEmptyState();
      return;
    }

    // For adds (or unknown actions) fall back to a full render so any new
    // item gets fetched and inserted. We still skip recreating items that
    // are already in the list by short-circuiting in #renderItems when the
    // current DOM already matches the storage state.
    this.#render();
  };

  /**
   * Looks up an element by its `ref` attribute via a live querySelector so we
   * always resolve against the current DOM, even after a section morph has
   * replaced our previously-cached references.
   *
   * @param {string} name
   * @returns {HTMLElement | null}
   */
  #findRef(name) {
    return /** @type {HTMLElement | null} */ (this.querySelector(`[ref="${name}"]`));
  }

  #renderCountBadge() {
    const count = this.#findRef('count');
    if (!count) return;
    const total = Wishlist.getCount();
    count.textContent = total > 99 ? '99+' : String(total);
    count.toggleAttribute('hidden', total === 0);
    this.toggleAttribute('data-has-items', total > 0);
  }

  async #render() {
    const items = Wishlist.getItems();

    // The recommendations carousel + "Verder winkelen" CTA are always
    // visible — only the "Je wishlist is leeg" copy is conditional.
    if (items.length === 0) {
      this.#showEmptyState();
      return;
    }

    this.#hideEmptyState();
    await this.#renderItems(items);
  }

  #showEmptyState() {
    const list = this.#findRef('list');
    const emptyState = this.#findRef('emptyState');
    if (list) list.innerHTML = '';
    if (emptyState) emptyState.hidden = false;
  }

  #hideEmptyState() {
    const emptyState = this.#findRef('emptyState');
    if (emptyState) emptyState.hidden = true;
  }

  /**
   * Renders the wishlist items into the list container. Always queries for
   * the current `list` element so the function is safe to call repeatedly
   * across mounts, morphs and visibility changes.
   *
   * @param {{ id: string, handle: string }[]} items
   */
  async #renderItems(items) {
    const list = this.#findRef('list');
    if (!list) return;

    list.innerHTML = '';
    const fragment = document.createDocumentFragment();

    // Show skeletons immediately for instant feedback.
    items.forEach(() => fragment.appendChild(this.#createSkeleton()));
    list.appendChild(fragment);

    // Fetch products in parallel and replace skeletons with real cards.
    const skeletons = Array.from(list.children);
    await Promise.all(
      items.map(async (item, index) => {
        const skeleton = skeletons[index];
        try {
          const product = await this.#fetchProduct(item.handle);
          if (!product) {
            skeleton.remove();
            return;
          }
          const card = this.#renderItem(product, item);
          if (card && skeleton.parentNode) {
            skeleton.parentNode.replaceChild(card, skeleton);
          }
        } catch (_error) {
          skeleton.remove();
        }
      })
    );

    // If every fetch failed, fall back to the empty state copy.
    if (list.children.length === 0) {
      this.#showEmptyState();
    }
  }

  /**
   * Returns a skeleton placeholder element.
   * @returns {HTMLElement}
   */
  #createSkeleton() {
    const li = document.createElement('li');
    li.className = 'wishlist-drawer__item wishlist-drawer__item--skeleton';
    li.innerHTML = `
      <div class="wishlist-drawer__media wishlist-drawer__media--skeleton" aria-hidden="true"></div>
      <div class="wishlist-drawer__info">
        <span class="wishlist-drawer__skeleton-line" aria-hidden="true"></span>
        <span class="wishlist-drawer__skeleton-line wishlist-drawer__skeleton-line--short" aria-hidden="true"></span>
      </div>
    `;
    return li;
  }

  /**
   * Fetches product JSON via Shopify's storefront endpoint.
   * @param {string} handle
   * @returns {Promise<any|null>}
   */
  async #fetchProduct(handle) {
    if (!handle) return null;
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const response = await fetch(`${root}products/${encodeURIComponent(handle)}.js`);
    if (!response.ok) return null;
    return response.json();
  }

  /**
   * Renders a single wishlist item from product JSON.
   * @param {any} product
   * @param {{ id: string, handle: string, variantId?: string }} item
   */
  #renderItem(product, item) {
    const li = document.createElement('li');
    li.className = 'wishlist-drawer__item';
    li.dataset.productId = String(product.id);

    const variant =
      (item.variantId && product.variants.find((v) => String(v.id) === String(item.variantId))) ||
      product.variants.find((v) => v.available) ||
      product.variants[0];

    const image = (variant && variant.featured_image && variant.featured_image.src) || product.featured_image || '';
    const url = variant ? `${product.url}?variant=${variant.id}` : product.url;
    const price = this.#formatMoney(variant ? variant.price : product.price);
    const compareAt = this.#formatMoney(variant ? variant.compare_at_price : product.compare_at_price);
    const onSale = variant ? variant.compare_at_price && variant.compare_at_price > variant.price : false;

    const removeLabel = this.dataset.labelRemove || 'Remove from wishlist';
    const addToCartLabel = this.dataset.labelAddToCart || 'Add to cart';
    const soldOutLabel = this.dataset.labelSoldOut || 'Sold out';

    const variantId = variant && variant.id;
    const canAddToCart = !!variant && variant.available;

    li.innerHTML = `
      <a class="wishlist-drawer__media-link" href="${url}" aria-label="${this.#escape(product.title)}">
        ${image ? `<img class="wishlist-drawer__media" src="${this.#sized(image, 240)}" alt="${this.#escape(product.title)}" loading="lazy" width="120" height="150">` : '<div class="wishlist-drawer__media wishlist-drawer__media--placeholder"></div>'}
      </a>
      <div class="wishlist-drawer__info">
        <a class="wishlist-drawer__title" href="${url}">${this.#escape(product.title)}</a>
        <div class="wishlist-drawer__price">
          ${onSale ? `<s class="wishlist-drawer__compare">${compareAt}</s> ` : ''}
          <span class="wishlist-drawer__current ${onSale ? 'wishlist-drawer__current--sale' : ''}">${price}</span>
        </div>
        <div class="wishlist-drawer__actions">
          ${
            canAddToCart
              ? `<button type="button" class="button button--secondary wishlist-drawer__add" data-variant-id="${variantId}">${this.#escape(addToCartLabel)}</button>`
              : `<span class="wishlist-drawer__soldout">${this.#escape(soldOutLabel)}</span>`
          }
          <button type="button" class="button-unstyled wishlist-drawer__remove" data-product-id="${product.id}" aria-label="${this.#escape(removeLabel)}" title="${this.#escape(removeLabel)}">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" width="16" height="16" aria-hidden="true">
              <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width, 1.5)" d="M5 5l10 10M15 5L5 15"/>
            </svg>
          </button>
        </div>
      </div>
    `;

    // Click handling is delegated at the drawer level (see
    // #handleDelegatedClick in connectedCallback), so we don't attach
    // per-button listeners here — that would survive normal renders but
    // not a section-morph that swaps the entire item subtree.
    return li;
  }

  /**
   * Adds a wishlist item to the cart, matching the request format that the
   * theme's own product-form.js uses so we play nicely with cart-items section
   * rendering and the existing cart drawer animation.
   *
   * @param {HTMLButtonElement} button - The add-to-cart button that was clicked.
   * @param {string | number} variantId
   */
  async #handleAddToCart(button, variantId) {
    if (!variantId) return;

    const original = button.textContent;
    button.disabled = true;
    button.classList.add('is-loading');

    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    const cartAddUrl =
      (window.Theme && Theme.routes && Theme.routes.cart_add_url) || `${root}cart/add.js`;

    // Collect cart-items section IDs so Shopify returns the refreshed HTML
    // in one round-trip and we don't need a separate section render.
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionIds.push(item.dataset.sectionId);
      }
    });

    // Use application/x-www-form-urlencoded — the most universally accepted
    // body format for /cart/add.js. Matches what the theme's own product-form
    // submission produces under the hood and avoids the CSRF/session quirks
    // that the Shopify CLI dev proxy throws at multipart and JSON POSTs.
    const formBody = new URLSearchParams();
    formBody.append('id', String(variantId));
    formBody.append('quantity', '1');
    if (sectionIds.length) {
      formBody.append('sections', sectionIds.join(','));
    }
    formBody.append('sections_url', window.location.pathname);

    console.log('[wishlist] POST', cartAddUrl, 'body:', formBody.toString());

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        body: formBody.toString(),
      });
      console.log('[wishlist] response status:', response.status);
      const data = await response.json().catch(() => null);

      if (!response.ok || (data && data.status && data.status >= 400)) {
        throw new Error((data && data.message) || `Add to cart failed (${response.status})`);
      }

      // Pull the latest cart so we can dispatch a CartAddEvent with the
      // updated item count. The cart-icon listens for this and animates.
      let cart = null;
      try {
        cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
          .then((r) => r.json());
      } catch (_error) {
        // Failing to refresh the cart count shouldn't break the UI feedback.
      }

      // Suppress the cart-drawer auto-open ONLY around the synchronous event
      // dispatch. Patrick wants the visitor to stay in the wishlist drawer
      // with a toast confirmation; the cart drawer should not pop over it.
      // Stripping and restoring the attribute synchronously around the
      // dispatch keeps every other add-to-cart flow (PDP, quick-add, etc.)
      // exactly as the theme intended.
      const cartDrawer = document.querySelector('cart-drawer-component');
      const hadAutoOpen = !!(cartDrawer && cartDrawer.hasAttribute('auto-open'));
      try {
        if (hadAutoOpen) cartDrawer.removeAttribute('auto-open');
        document.dispatchEvent(
          new CartAddEvent(cart || {}, 'wishlist-drawer', {
            source: 'wishlist-drawer',
            itemCount: cart ? cart.item_count : undefined,
            variantId: String(variantId),
            sections: data && data.sections,
          })
        );
      } finally {
        if (hadAutoOpen) cartDrawer.setAttribute('auto-open', '');
      }

      this.#showToast(this.dataset.labelAddedToast || this.dataset.labelAdded || 'Toegevoegd aan winkelmand');

      // After the cart-items section morph, our original `button` reference may
      // point at a detached node. Look up the live button by its variantId so
      // the "Added" → original-text transition is visible to the user.
      const liveButton = this.querySelector(
        `.wishlist-drawer__add[data-variant-id="${variantId}"]`
      );
      const targetButton = liveButton || button;
      const targetOriginal = liveButton ? liveButton.textContent : original;

      targetButton.textContent = this.dataset.labelAdded || 'Added';
      setTimeout(() => {
        const finalButton = this.querySelector(
          `.wishlist-drawer__add[data-variant-id="${variantId}"]`
        );
        const buttonToReset = finalButton || targetButton;
        buttonToReset.textContent = targetOriginal;
        buttonToReset.disabled = false;
        buttonToReset.classList.remove('is-loading');
      }, 1200);
    } catch (error) {
      // Surface a friendly retry state — and log for debugging.
      console.error('[wishlist] add to cart failed:', error);
      const liveButton = this.querySelector(
        `.wishlist-drawer__add[data-variant-id="${variantId}"]`
      );
      const buttonToReset = liveButton || button;
      buttonToReset.disabled = false;
      buttonToReset.classList.remove('is-loading');
      buttonToReset.textContent = original;
    }
  }

  /** @type {number | undefined} */
  #toastTimer;

  /**
   * Shows a transient confirmation toast in the drawer footer.
   * @param {string} message
   */
  #showToast(message) {
    // Always look up the toast element fresh — the cart-items section morph
    // that runs synchronously during our CartAddEvent dispatch can replace
    // children of the drawer, leaving any cached ref pointing at a detached
    // element. `#findRef` queries the live DOM so we always hit the visible one.
    const toast = this.#findRef('toast');
    if (!toast) return;
    toast.textContent = message;
    toast.hidden = false;
    // Force reflow so the .is-visible transition runs even on rapid re-trigger.
    void toast.offsetWidth;
    toast.classList.add('is-visible');

    if (this.#toastTimer) clearTimeout(this.#toastTimer);
    this.#toastTimer = window.setTimeout(() => {
      const liveToast = this.#findRef('toast');
      if (liveToast) liveToast.classList.remove('is-visible');
      this.#toastTimer = window.setTimeout(() => {
        const finalToast = this.#findRef('toast');
        if (finalToast) finalToast.hidden = true;
      }, 250);
    }, 2200);
  }

  /**
   * Naïef format-helper — voor sieraden zonder valutavarianten ruim voldoende.
   * @param {number} cents
   */
  #formatMoney(cents) {
    if (cents == null) return '';
    const value = (cents / 100).toFixed(2);
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'EUR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'nl-NL', {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch (_error) {
      return `${currency} ${value}`;
    }
  }

  /**
   * @param {string} src
   * @param {number} width
   */
  #sized(src, width) {
    if (!src) return '';
    const url = src.startsWith('//') ? `https:${src}` : src;
    return url.replace(/(\.(jpg|jpeg|png|webp|gif))(\?.*)?$/i, `_${width}x$1$3`);
  }

  /** @param {string} value */
  #escape(value) {
    return String(value ?? '').replace(/[&<>"']/g, (char) => {
      switch (char) {
        case '&':
          return '&amp;';
        case '<':
          return '&lt;';
        case '>':
          return '&gt;';
        case '"':
          return '&quot;';
        default:
          return '&#39;';
      }
    });
  }
}

if (!customElements.get('wishlist-drawer-component')) {
  customElements.define('wishlist-drawer-component', WishlistDrawerComponent);
}
